/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { addDays, addHours, format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TableList, Container, Filter, Status } from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import Loading from '~/components/Loading';
import { IOption } from '~/components/Select';

interface IDataItem {
  id: string;
  organizer: string;
  event: string;
  date: string;
  tickets_qty: number;
  total: string;
  payment: string;
  parcels: number;
  status_payment: string;
  status_event: string;
  to_receive: string;
  active: boolean;
}

interface IItem {
  id: number;
}

interface IOrderData {
  courtesy_id: any;
  voucher: {
    code: string;
  };
  id: number;
  total: string;
  code: string;
  status: string;
  created_at: string;
  items: IItem[];
  organizer_id: number;
  event_id: number;
  service_fee: string;
  processing_fee: string;
  discount: string;
  event: {
    name: string;
    date: string;
    status_id: number;
    user: {
      name: string;
    };
    status: {
      name: string;
    };
    organizer: {
      name: string;
    };
  };
  payment: {
    id: number;
    payment_type: string;
    organizer: string;
    status: string;
    installment: number;
    due_date: string;
    payment_date: string;
    total: string;
    gateway: string;
    koro: string;
    organizer_deadline: number;
    koro_deadline: number;
  };
  user: {
    id: number;
    name: string;
    email: string;
  };
}

interface IOrderResponse {
  data: IOrderData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IOrganizer {
  id: number;
  name: string;
}

const Payments: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IDataItem[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [eventName, setEventName] = useState<string | undefined>(undefined);
  const [statusSelected, setStatusSelected] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [quantity, setQuantity] = useState(10);
  const [organizerId, setOrganizerId] = useState<string | undefined>(undefined);
  const [codeVoucher, setCodeVoucher] = useState<string | undefined>();
  const [orderFilter, setOrderFilter] = useState<string | undefined>();

  const timeZone = 'Etc/GMT';

  useEffect(() => {
    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  const status = useMemo(
    () => [
      {
        id: 'Pago',
        value: 'Pago',
        selected: false,
      },
      {
        id: 'Aguardando pagamento',
        value: 'Aguardando pagamento',
        selected: false,
      },
      {
        id: 'Cancelado',
        value: 'Cancelado',
        selected: false,
      },
      {
        id: 'Expirado',
        value: 'Expirado',
        selected: false,
      },
      {
        id: 'Erro no pagamento',
        value: 'Erro no pagamento',
        selected: false,
      },
    ],
    []
  );

  const handleLoadOrders = useCallback(
    async (
      pageData,
      user?: string,
      event?: string,
      statusData?: string,
      quantityData?: number,
      organizer_id?: string,
      codeVoucherData?: string,
      orderData?: string
    ) => {
      setLoading(true);
      const response = await api
        .get<IOrderResponse>('orders/admins', {
          params: {
            page: pageData,
            user,
            event,
            status: statusData,
            quantity: quantityData,
            organizer_id,
            voucher: codeVoucherData,
            order_id: orderData?.toString().slice(12) || orderData,
          },
        })
        .finally(() => {
          setLoading(false);
        });

      const dataAux = response.data.data.map<IDataItem>((order) => {
        const to_receive =
          parseFloat(order.total) - parseFloat(order.total) * 0.03;

        let subData = {
          organizer: '-',
          event: '-',
          event_date: '-',
          event_status_id: '-',
          event_status: '-',
          status_event: '-',
        };

        if (order.event) {
          subData = {
            organizer: order.event.organizer?.name,
            event: order.event.name,
            event_date: format(
              addHours(parseISO(order.event.date), 3),
              'dd/MM/yyyy'
            ),
            event_status_id: order.event.status_id?.toString(),
            event_status: order.event.status
              ? order.event.status.name
              : 'Cadastrando',
            status_event: order.event.status
              ? order.event.status.name
              : 'Cadastrando',
          };
        }

        return {
          ...subData,
          id: order.code,

          buyer_name: order.user.name,
          date: formatInTimeZone(
            parseISO(order.created_at),
            timeZone,
            'dd/MM/yyyy HH:mm:ss'
          ),
          tickets_qty: order.items.length,
          voucher: order.voucher?.code ?? '-',
          total: formatPrice(
            parseFloat(
              order.payment
                ? order.payment.payment_type.toLowerCase() === 'free'
                  ? '0'
                  : order.payment.total
                : '0'
            )
          ),
          payment: order.courtesy_id
            ? 'Cortesia'
            : order.payment?.payment_type.toLowerCase().includes('credit')
            ? 'Cartão de Crédito'
            : order.payment?.payment_type.toLowerCase() === 'free'
            ? 'Grátis'
            : 'PIX',
          payment_id: order.payment?.id,
          organizer_id: order.organizer_id,
          event_id: order.event_id,

          user_id: order.user.id,
          buyer_email: order.user.email,
          due_date: order.payment?.due_date
            ? format(parseISO(order.payment?.due_date), 'dd/MM/yyyy')
            : '-',
          payment_date: order.payment?.payment_date
            ? format(parseISO(order.payment?.payment_date), 'dd/MM/yyyy')
            : '-',
          service_fee: formatPrice(parseFloat(order.service_fee)),
          processing_fee: formatPrice(parseFloat(order.processing_fee)),
          discount: formatPrice(parseFloat(order.discount)),
          organizer_value: formatPrice(
            parseFloat(order.payment?.organizer || '0')
          ),
          gateway_value: formatPrice(parseFloat(order.payment?.gateway || '0')),
          koro_value: formatPrice(parseFloat(order.payment?.koro || '0')),
          organizer_deadline: order.payment?.organizer_deadline || 0,
          koro_deadline: order.payment?.koro_deadline || 0,
          organizer_release_date: order.payment?.payment_date
            ? format(
                addDays(
                  parseISO(order.payment?.payment_date),
                  order.payment?.organizer_deadline
                ),
                'dd/MM/yyyy'
              )
            : '-',
          koro_release_date: order.payment?.payment_date
            ? format(
                addDays(
                  parseISO(order.payment?.payment_date),
                  order.payment?.koro_deadline
                ),
                'dd/MM/yyyy'
              )
            : '-',
          parcels: order.payment?.installment || 0,
          status_payment: order.status,

          to_receive: formatPrice(to_receive),
          code: order.code,
          active: true,
        };
      });

      setData(dataAux);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    []
  );

  useEffect(() => {
    handleLoadOrders(1);
  }, [handleLoadOrders]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: 'Pedido',
        selector: 'id',
        sortable: true,
      },
      {
        name: 'Data Venda',
        selector: 'date',
        sortable: true,
      },
      {
        name: 'Inscrições',
        selector: 'tickets_qty',
        sortable: true,
      },
      {
        name: 'Cupom',
        selector: 'voucher',
        sortable: true,
      },
      {
        name: 'Status pagto',
        selector: 'status_payment',
        sortable: true,
        cell: (row) => (
          <Status
            status={row.status_payment}
            className="text-white rounded-pill mb-0"
          >
            {row.status_payment}
          </Status>
        ),
      },
      {
        name: 'ID Pag',
        selector: 'payment_id',
        sortable: true,
      },

      {
        name: 'ID Org',
        selector: 'organizer_id',
        sortable: true,
      },

      {
        name: 'Organizador',
        selector: 'organizer',
        sortable: true,
      },
      {
        name: 'ID Eve',
        selector: 'event_id',
        sortable: true,
      },

      {
        name: 'Evento',
        selector: 'event',
        sortable: true,
      },
      {
        name: 'Data Eve',
        selector: 'event_date',
        sortable: true,
      },
      {
        name: 'ID sta Eve',
        selector: 'event_status_id',
        sortable: true,
      },
      {
        name: 'Status Eve',
        selector: 'event_status',
        sortable: true,
      },
      {
        name: 'ID Usu',
        selector: 'user_id',
        sortable: true,
      },
      {
        name: 'Nome comprador',
        selector: 'buyer_name',
        sortable: true,
      },
      {
        name: 'Email comprador',
        selector: 'buyer_email',
        sortable: true,
      },
      {
        name: 'Forma pgto',
        selector: 'payment',
        sortable: true,
      },
      {
        name: 'Data vencimento',
        selector: 'due_date',
        sortable: true,
      },
      {
        name: 'Data pagto',
        selector: 'payment_date',
        sortable: true,
      },
      {
        name: 'Total$',
        selector: 'total',
        sortable: true,
      },
      {
        name: 'Tax Serv',
        selector: 'service_fee',
        sortable: true,
      },
      {
        name: 'Tax Proc',
        selector: 'processing_fee',
        sortable: true,
      },
      {
        name: 'Vl Desc$',
        selector: 'discount',
        sortable: true,
      },
      {
        name: 'Vl Org$',
        selector: 'organizer_value',
        sortable: true,
      },
      {
        name: 'Vl Gate$',
        selector: 'gateway_value',
        sortable: true,
      },
      {
        name: 'Vl Koro$',
        selector: 'koro_value',
        sortable: true,
      },
      {
        name: 'Dias Org',
        selector: 'organizer_deadline',
        sortable: true,
      },
      {
        name: 'Dias Koro',
        selector: 'koro_deadline',
        sortable: true,
      },

      {
        name: 'Dt Lib Org',
        selector: 'organizer_release_date',
        sortable: true,
      },
      {
        name: 'Dt Lib Koro',
        selector: 'koro_release_date',
        sortable: true,
      },
    ],
    []
  );

  const handleChangePage = useCallback(
    (page) => {
      if (page <= tableData.totalPage) {
        handleLoadOrders(
          page,
          userName,
          eventName,
          statusSelected,
          quantity,
          organizerId,
          codeVoucher,
          orderFilter
        );
        setPageSelected(page);
      }
    },
    [
      eventName,
      handleLoadOrders,
      organizerId,
      quantity,
      statusSelected,
      tableData.totalPage,
      userName,
      codeVoucher,
      orderFilter,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setUserName(formData.name);
      setEventName(formData.event === 'Todos' ? undefined : formData.event);
      setStatusSelected(
        formData.statusPayment !== 'todos' ? formData.statusPayment : undefined
      );
      setOrganizerId(
        formData.organizer !== 'todos' ? formData.organizer : undefined
      );
      setCodeVoucher(formData.codeVoucher || undefined);
      setOrderFilter(formData.order || undefined);
      handleLoadOrders(
        1,
        formData.name,
        formData.event === 'Todos' ? undefined : formData.event,
        formData.statusPayment !== 'todos' ? formData.statusPayment : undefined,
        quantity,
        formData.organizer !== 'todos' ? formData.organizer : undefined,
        formData.codeVoucher || undefined,
        formData.order || undefined
      );
      setPageSelected(1);
    },
    [handleLoadOrders, quantity]
  );

  const handleClickExportCsv = useCallback(async () => {
    setLoading(true);
    const response = await api
      .get<IOrderData[]>('orders/admins', {
        params: {
          user: userName,
          event: eventName,
          status: statusSelected,
          organizer_id: organizerId,
          voucher: codeVoucher,
          order_id: orderFilter?.toString().slice(12) || orderFilter,
        },
      })
      .finally(() => setLoading(false));

    const dataAux = response.data.map<IDataItem>((order) => {
      const to_receive =
        parseFloat(order.total) - parseFloat(order.total) * 0.03;

      let subData = {
        organizer: '-',
        event: '-',
        event_date: '-',
        event_status_id: '-',
        event_status: '-',
        status_event: '-',
      };

      if (order.event) {
        subData = {
          organizer: order.event.organizer?.name,
          event: order.event.name,
          event_date: format(
            addHours(parseISO(order.event.date), 3),
            'dd/MM/yyyy'
          ),
          event_status_id: order.event.status_id?.toString(),
          event_status: order.event.status
            ? order.event.status.name
            : 'Cadastrando',
          status_event: order.event.status
            ? order.event.status.name
            : 'Cadastrando',
        };
      }

      return {
        ...subData,
        id: `#${order.code}`,
        date: formatInTimeZone(
          parseISO(order.created_at),
          timeZone,
          'dd/MM/yyyy HH:mm:ss'
        ),
        tickets_qty: order.items.length,
        voucher: order.voucher?.code ?? '-',
        status_payment: order.status,
        payment_id: order.payment?.id,
        organizer_id: order.organizer_id,

        event_id: order.event_id,

        user_id: order.user.id,
        buyer_name: order.user.name,
        buyer_email: order.user.email,
        payment: order.courtesy_id
          ? 'Cortesia'
          : order.payment?.payment_type.toLowerCase().includes('credit')
          ? 'Cartão de Crédito'
          : order.payment?.payment_type.toLowerCase() === 'free'
          ? 'Grátis'
          : 'PIX',
        due_date: order.payment?.due_date
          ? format(parseISO(order.payment?.due_date), 'dd/MM/yyyy')
          : '-',
        payment_date: order.payment?.payment_date
          ? format(parseISO(order.payment?.payment_date), 'dd/MM/yyyy')
          : '-',
        total: parseFloat(
          order.payment
            ? order.payment.payment_type.toLowerCase() === 'free'
              ? '0'
              : order.payment.total
            : '0'
        )
          .toFixed(2)
          .replace('.', ','),
        service_fee: parseFloat(order.service_fee).toFixed(2).replace('.', ','),
        processing_fee: parseFloat(order.processing_fee)
          .toFixed(2)
          .replace('.', ','),
        discount: parseFloat(order.discount).toFixed(2).replace('.', ','),
        organizer_value: parseFloat(order.payment?.organizer || '0')
          .toFixed(2)
          .replace('.', ','),
        gateway_value: parseFloat(order.payment?.gateway || '0')
          .toFixed(2)
          .replace('.', ','),
        koro_value: parseFloat(order.payment?.koro || '0')
          .toFixed(2)
          .replace('.', ','),
        organizer_deadline: order.payment?.organizer_deadline || 0,
        koro_deadline: order.payment?.koro_deadline || 0,
        organizer_release_date: order.payment?.payment_date
          ? format(
              addDays(
                parseISO(order.payment?.payment_date),
                order.payment?.organizer_deadline
              ),
              'dd/MM/yyyy'
            )
          : '-',
        koro_release_date: order.payment?.payment_date
          ? format(
              addDays(
                parseISO(order.payment?.payment_date),
                order.payment?.koro_deadline
              ),
              'dd/MM/yyyy'
            )
          : '-',

        parcels: order.payment?.installment || 0,

        to_receive: formatPrice(to_receive),
        active: true,
      };
    });

    return dataAux;
  }, [
    codeVoucher,
    eventName,
    orderFilter,
    organizerId,
    statusSelected,
    userName,
  ]);

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      handleLoadOrders(
        1,
        userName,
        eventName,
        statusSelected,
        quantityData,
        organizerId,
        codeVoucher,
        orderFilter
      );
      setQuantity(quantityData);
      setPageSelected(1);
    },
    [
      codeVoucher,
      eventName,
      handleLoadOrders,
      organizerId,
      statusSelected,
      userName,
      orderFilter,
    ]
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de pedidos</h1>
              <ExportCsv
                data={data}
                columns={columns}
                fileName="Lista_de_pedidos"
                onClick={handleClickExportCsv}
              />
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              event
              name
              order
              statusPayment
              paymentsData={status}
              organizer
              codeVoucher
              organizersData={organizers}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        changeQuantity
                        showTotals
                        generalTotal={tableData.generalTotal}
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        onChangeQuantity={handleChangeQuantity}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Payments;
